import { useContext, useEffect, useRef } from 'react';
import { Text } from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EditItem } from '@moko/ui';
import AppContext from '../AppContext';
import { successMessageAtom } from '../../atoms/messageBarAtoms';
import {
  createPricingItem,
  getPricingItem,
  IPricingItemBody,
  updatePricingItem,
} from '../../services/pricingService';
import { pricingItemsPath } from './pricingSettings';

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const toBody = (
  item: Record<string, unknown>
): IPricingItemBody | undefined => {
  const code = text(item.code);
  const name = text(item.name);
  const type = text(item.type);
  const quantityType = text(item.quantityType);
  if (!code || !name || !type || !quantityType) {
    return undefined;
  }
  const description = text(item.description);
  return {
    code,
    name,
    description: description || null,
    type,
    isRequired: item.isRequired === true,
    quantityType,
    exclusivityGroup: text(item.exclusivityGroup) || null,
  };
};

const PricingItemForm = () => {
  const isSaved = useRef(false);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const itemId = Number.parseInt(query.get('id') ?? '');
  const isEdit = !Number.isNaN(itemId) && itemId > 0;
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const context = useContext(AppContext);
  const title = isEdit ? 'Edit pricing item' : 'Add pricing item';

  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Text variant="xLarge">{title}</Text>
      <EditItem
        getAction={async (abortController, id) =>
          getPricingItem(abortController, id)
        }
        newAction={async (abortController, item) => {
          const body = toBody(item);
          if (!body) return;
          await createPricingItem(abortController, body);
          setSuccessMessage(`Pricing item ${body.code} created.`);
          navigate(pricingItemsPath);
        }}
        editAction={async (abortController, id, item) => {
          const body = toBody(item);
          if (!body) return;
          await updatePricingItem(abortController, id, body);
          setSuccessMessage(`Pricing item ${body.code} updated.`);
          navigate(pricingItemsPath);
        }}
        isSaved={isSaved}
        back={pricingItemsPath}
        confirmOnCancel={false}
        onCancel={() => navigate(pricingItemsPath)}
        defaultValues={
          isEdit ? undefined : JSON.stringify({ isRequired: true })
        }
        metadata={{
          fields: [
            {
              name: 'code',
              fieldType: 'String',
              label: 'Code',
              mandatory: true,
            },
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
              mandatory: true,
            },
            { name: 'description', fieldType: 'String', label: 'Description' },
            {
              name: 'type',
              fieldType: 'Lookup',
              label: 'Type',
              lookupList: 'PricingItemTypes',
              mandatory: true,
            },
            {
              name: 'quantityType',
              fieldType: 'Lookup',
              label: 'Quantity type',
              lookupList: 'QuantityTypes',
              mandatory: true,
            },
            {
              name: 'exclusivityGroup',
              fieldType: 'String',
              label: 'Exclusivity group',
            },
            { name: 'isRequired', fieldType: 'Checkbox', label: 'Required' },
          ],
          lookups: [
            {
              name: 'PricingItemTypes',
              values: [
                { key: 'Service', value: 'Service' },
                { key: 'AddOn', value: 'AddOn' },
              ],
            },
            {
              name: 'QuantityTypes',
              values: [
                { key: 'Fixed', value: 'Fixed' },
                { key: 'AssetCount', value: 'AssetCount' },
              ],
            },
          ],
          validations: [
            {
              field: 'code',
              rules: [{ rule: 'NE', errorMessage: 'Code is required.' }],
            },
            {
              field: 'name',
              rules: [{ rule: 'NE', errorMessage: 'Name is required.' }],
            },
            {
              field: 'type',
              rules: [{ rule: 'NE', errorMessage: 'Type is required.' }],
            },
            {
              field: 'quantityType',
              rules: [
                { rule: 'NE', errorMessage: 'Quantity type is required.' },
              ],
            },
            {
              field: 'exclusivityGroup',
              rules: [
                {
                  rule: 'NGT30',
                  errorMessage:
                    'Exclusivity group must be 30 characters or less.',
                },
              ],
            },
          ],
          interactions: [],
        }}
      />
    </div>
  );
};

export default PricingItemForm;
